import {
  createDraftSafeSelector,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit'
import storage from 'redux-persist/es/storage'
import { persistReducer } from 'redux-persist'

import { RootState } from '..'
import { NameSpace } from '../types'

interface SoundLinkHintInterface {
  closedDate: string
  closedState: boolean
}

interface AlertState {
  approvedAlertsIds: Array<number>
  soundLinkHint: SoundLinkHintInterface
  placementGuideStep: number
  run: boolean
  closedMessageCampaignIds: Array<number>
  dismissedUpsellCampaignIds: Array<number>
  runReferralGuide?: boolean
  runAdsGuide?: boolean
  runThemeTooltip?: boolean
  runOnboardingGuide?: boolean
  runAutoRenewHint?: boolean
  runPreReleaseHint?: boolean
  completionGuideGranted?: boolean
}

const initialState: AlertState = {
  approvedAlertsIds: [],
  soundLinkHint: {
    closedDate: new Date().toISOString() || '',
    closedState: true,
  },
  placementGuideStep: 0,
  run: false,
  closedMessageCampaignIds: [],
  dismissedUpsellCampaignIds: [],
  runReferralGuide: false,
  runAdsGuide: false,
  runThemeTooltip: false,
  runOnboardingGuide: false,
  runAutoRenewHint: false,
  runPreReleaseHint: false,
  completionGuideGranted: false,
}

const alerts = createSlice({
  initialState,
  name: NameSpace.Alert,
  reducers: {
    setRunAdsGuide: (state, { payload }: PayloadAction<boolean>) => {
      state.runAdsGuide = payload
    },
    setRunThemeTooltip: (state, { payload }: PayloadAction<boolean>) => {
      state.runThemeTooltip = payload
    },
    setRunAutoRenewHint: (state, { payload }: PayloadAction<boolean>) => {
      state.runAutoRenewHint = payload
    },
    setRunPreReleaseHint: (state, { payload }: PayloadAction<boolean>) => {
      state.runPreReleaseHint = payload
    },
    setRunOnboardingGuide: (state, { payload }: PayloadAction<boolean>) => {
      state.runOnboardingGuide = payload
    },
    setPlacementGuideStep: (state, { payload }: PayloadAction<number>) => {
      state.placementGuideStep = payload
    },
    setRunReferralGuide: (state, { payload }: PayloadAction<boolean>) => {
      state.runReferralGuide = payload
    },
    setBonusGuideRun: (state, { payload }: PayloadAction<boolean>) => {
      state.run = payload
    },
    setApprovedAlertsList: (state, { payload }: PayloadAction<number[]>) => {
      state.approvedAlertsIds = payload
    },
    setSoundLinkHint: (
      state,
      { payload }: PayloadAction<SoundLinkHintInterface>,
    ) => {
      state.soundLinkHint = payload
    },
    setClosedMessageCampaignId: (state, { payload }: PayloadAction<number>) => {
      state.closedMessageCampaignIds = [
        ...state.closedMessageCampaignIds,
        payload,
      ]
    },
    setDismissedUpsellCampaignId: (
      state,
      { payload }: PayloadAction<number>,
    ) => {
      state.dismissedUpsellCampaignIds = [
        ...state.dismissedUpsellCampaignIds,
        payload,
      ]
    },
    setCompletionGuideGranted: (state, { payload }: PayloadAction<boolean>) => {
      state.completionGuideGranted = payload
    },
  },
})

const persistConfig = {
  storage,
  key: 'alerts',
  blacklist: ['run', 'dismissedUpsellCampaignIds'],
}
export const alertProcess = persistReducer(persistConfig, alerts.reducer)

export const {
  setApprovedAlertsList,
  setSoundLinkHint,
  setPlacementGuideStep,
  setBonusGuideRun,
  setClosedMessageCampaignId,
  setRunReferralGuide,
  setRunAdsGuide,
  setRunThemeTooltip,
  setRunAutoRenewHint,
  setRunPreReleaseHint,
  setRunOnboardingGuide,
  setDismissedUpsellCampaignId,
  setCompletionGuideGranted,
} = alerts.actions

const getState = (state: RootState): AlertState => state.alerts
export const getPlacementGuideStep = createDraftSafeSelector(
  getState,
  ({ placementGuideStep }) => placementGuideStep,
)

export const getRunReferralGuide = createDraftSafeSelector(
  getState,
  ({ runReferralGuide }) => runReferralGuide,
)

export const getRunAdsGuide = createDraftSafeSelector(
  getState,
  ({ runAdsGuide }) => runAdsGuide,
)

export const getRunThemeTooltip = createDraftSafeSelector(
  getState,
  ({ runThemeTooltip }) => runThemeTooltip,
)

export const getRunAutoRenewHint = createDraftSafeSelector(
  getState,
  ({ runAutoRenewHint }) => runAutoRenewHint,
)

export const getRunPreReleaseHint = createDraftSafeSelector(
  getState,
  ({ runPreReleaseHint }) => runPreReleaseHint,
)

export const getApprovedAlertsIds = createDraftSafeSelector(
  getState,
  ({ approvedAlertsIds }) => approvedAlertsIds,
)

export const getSoundLinkHint = createDraftSafeSelector(
  getState,
  ({ soundLinkHint }) => soundLinkHint,
)

export const getClosedMessageCampaignIds = createDraftSafeSelector(
  getState,
  ({ closedMessageCampaignIds }) => closedMessageCampaignIds,
)

export const getDismissedUpsellCampaignIds = createDraftSafeSelector(
  getState,
  ({ dismissedUpsellCampaignIds }) => dismissedUpsellCampaignIds,
)

export const getCompletionGuideGranted = createDraftSafeSelector(
  getState,
  ({ completionGuideGranted }) => completionGuideGranted,
)
